/**
 * Rarity badge tint resolver. Kept out of `RevealBackground` (which is a
 * component) so it lives in its own non-component module — `WinnerStep`,
 * the badge backdrop, the glow overlay and the background tint all use it.
 */

/** Hex fallbacks used inside `var(--ui-badge-*, …)` so themes that
 *  haven't redefined the rarity colours still render the original
 *  Figma palette. Kept in sync with `:root { --ui-badge-* }` in
 *  `index.css` — change one, change both. */
const BADGE_FALLBACKS: Record<string, string> = {
  Grail:     '#bb9114',
  Chase:     '#F59E0B',
  Epic:      '#25EA1E',
  Rare:      '#F6D730',
  Uncommon:  '#A0BCDB',
  Common:    '#E5A015',
  'Big Hit': '#FFFFFF',
};

const BADGE_SLUGS: Record<string, string> = {
  Grail:     'grail',
  Chase:     'chase',
  Epic:      'epic',
  Rare:      'rare',
  Uncommon:  'uncommon',
  Common:    'common',
  'Big Hit': 'big-hit',
};

const DEFAULT_FALLBACK = '#7D7AF3';

/**
 * Returns a CSS-color expression for the badge tint. Outputs
 * `var(--ui-badge-<slug>, <fallback>)` so Design Constructor can
 * override the rarity colour without touching this code. Consumers
 * that need alpha must wrap the result in `color-mix(in srgb, … X%,
 * transparent)` — string concatenation with hex+alpha won't work
 * once the value is a CSS-var. */
export function glowColorFor(badge?: string | null): string {
  if (!badge) return `var(--ui-badge-default, ${DEFAULT_FALLBACK})`;
  const slug = BADGE_SLUGS[badge];
  const fallback = BADGE_FALLBACKS[badge] ?? DEFAULT_FALLBACK;
  return slug
    ? `var(--ui-badge-${slug}, ${fallback})`
    : fallback;
}

/** Tiers that don't get the premium reveal treatment (tint, backdrop,
 *  contour comet, rare highlight). The backend sends a badge for EVERY
 *  tier — this name check is the only gate keeping the reveal neutral
 *  for low pulls. */
const LOW_TIERS = new Set(['common', 'uncommon']);

/**
 * True for Rare and above — Rare / Epic / Legendary / Grail and specials like
 * Chase / Big Hit. False for Common, Uncommon, or no badge.
 */
export function isRarePlus(badge?: string | null): boolean {
  if (!badge) return false;
  return !LOW_TIERS.has(badge.trim().toLowerCase());
}
